
import he from 'he';
import DOMPurify from 'isomorphic-dompurify';
import React from 'react';
import { ICONS } from '@/public/img/icons';

// #region RENDER UNDERLINE
/**
 * Helper to render underline title from data into traductions files
 */
// TODO: remove txt & underline props when every pages are ported to strapi.
export const renderUnderline = (el: { text: string; txt?: string; isUnderlined: boolean; underline?: boolean }[] | [{ [index: string]: string; }]) => (
	el.map((content, key) => {
		if (content.text) {
			return content.isUnderlined ? (
				<React.Fragment key={key}> <em>{content.text}</em> </React.Fragment>
			) : (
				<span key={key}>{content.text}</span>
			);
		} else if (content.txt) {
			return content.underline ? (
				<React.Fragment key={key}> <em>{content.txt}</em> </React.Fragment>
			) : (
				<span key={key}>{content.txt}</span>
			);
		}
	})
);

export const parseUnderlineFromString = (text: string): { text: string; isUnderlined: boolean }[] => {
	const regex = /__(.*?)__/g;
	const parts = text.split(regex);
	const result: { text: string; isUnderlined: boolean }[] = [];

	parts.forEach((part, index) => {
		if (index % 2 === 0) {
			result.push({ text: part, isUnderlined: false });
		} else {
			result.push({ text: part, isUnderlined: true });
		}
	});

	return result;
};
// #endregion

// #region RENDER ICONS
/**
 * Helper to render icons based on a list of icons
 */
export const renderIcons = (icon: ICONS, iconsList: { [index: string]: any }) => {
	if (iconsList[icon]) {
		return React.createElement(iconsList[icon], {
			block: icon,
			key: iconsList[icon]._uid,
		});
	}
};
// #endregion

// #region RENDER SCRIPT
/**
 * Helper to render Schema.org tags for SEO
 */
export const renderScript = (schemaOrg: [{ [index: string]: string }] | object) => (
	<script
		type='application/ld+json'
		dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaOrg) }}
	/>
);
// #endregion

// #region TRANSFORM TITLE TO ANCHOR ID
/**
 * Helper to transform a string to anchor, used in summary component.
 */
export const transformTitleToAnchorId = (stringToTransform: string): string => {
	const specialCharacterRegex = /[`~!@#$%^&*()_|+=;:'",.<>{}[\]\\/]/g;
	return (
		stringToTransform.replace(specialCharacterRegex, '').trim().split(' ').join('-').replace('--', '-')
	);
};
// #endregion

// #region TRANSFORM TITLE ARRAY TO STRING
/**
 * Helper to transform a title array to a string.
 */
export const transformTitleArrayToString = (titleArray: { isUnderlined: boolean; text: string }[]): string => (
	titleArray.map(({ text, isUnderlined }: { text: string; isUnderlined: boolean }) =>
		isUnderlined ? ` ${text} ` : text).join('')
);
// #endregion

// #region IS URL ABSOLUTE
/**
 * Helper to check if an URL is absolute or not.
 */
export const isUrlAbsolute = (path: string): boolean => {
	const rule = new RegExp('^(?:[a-z]+:)?//', 'i');

	return rule.test(path);
};
// #endregion

// #region IS URL ROOT RELATIVE
/**
 * Helper to check if a relative URL is root or not.
 */
export const isUrlRootRelative = (path: string): boolean => {
	const rule = new RegExp('^/', 'i');

	return rule.test(path);
};
// #endregion

// #region IS STRING WITH HTML/XML ELEMENT
/**
 * Helper to check if a string contains HTML or XML tags.
 * This is used to migrate from content written in markdown to HTML (CKEditor migration)
 */
export const isStringWithHtmlElement = (content: string): boolean => {
	// `[sS]`, not `[\s\S]`: the pattern is a string, so those escapes never reached the regex. What it
	// actually matches is a single letter followed by any run of `s` or `S`, which misses the normal
	// multi-letter and attribute-bearing tags — `<div>`, `<strong>`, `<p class="lead">` on its own —
	// while still matching `<p>`, the oddity `<ss>`, and `<p class="lead">text</p>` through its
	// closing `</p>`. Written out as it behaves; correcting it changes what counts as HTML, so it is
	// tracked separately rather than fixed here.
	const rule = new RegExp('</?[a-z][sS]*>', 'i');

	return rule.test(content);
};
// #endregion

// #region CLEAN HTML TAGS FROM TEXT
/**
 * Helper to clean HTML tags from text content while preserving the text content.
 * This function strips HTML tags and decodes HTML entities to return clean text.
 *
 * @param htmlContent - HTML string content that needs to be cleaned
 * @param maxLength - Optional maximum length for the cleaned text (useful for descriptions)
 * @returns Clean text content without HTML tags
 */
export const cleanHtmlToText = (htmlContent: string, maxLength?: number): string => {
	if (!htmlContent || typeof htmlContent !== 'string') {
		return '';
	}

	// First sanitize the HTML to remove any potentially harmful content
	const sanitized = DOMPurify.sanitize(htmlContent, {
		ALLOWED_TAGS: [], // Remove all HTML tags
		ALLOWED_ATTR: [], // Remove all attributes
		KEEP_CONTENT: true, // Keep the text content
	});

	// Remove any remaining whitespace artifacts and normalize spaces
	const cleaned = he.decode(sanitized)
		.replace(/\s+/g, ' ') // Replace multiple whitespace characters with single space
		.trim(); // Remove leading and trailing whitespace

	// Apply length limit if specified
	if (maxLength && cleaned.length > maxLength) {
		return cleaned.substring(0, maxLength).trim() + '...';
	}

	return cleaned;
};
// #endregion

// #region JOB IMAGE UTILITIES
/**
 * Get image URL for job postings with enhanced logic
 * Handles both job data (16:9, 1920px) and mission data (3:2, 480px) with proper fallbacks
 */
export const getJobImageUrl = (publication: any, job?: any): string | null => {
	// First try to get image from job data (16:9 format, 1920 size) - server-side data
	if (job?.image?.['16_9']?.['1920'] && publication.objectId) {
		const indexOfPublicationImage = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz'.indexOf(publication.objectId[0]) % job.image['16_9']['1920'].length;
		const selectedImage = job.image['16_9']['1920'][indexOfPublicationImage]['1x'];
		// Handle special case for specific image replacement
		return selectedImage === '/images/job/D1505_003_16_9_1920_1x.jpg'
			? '/images/job/z__D1505_003_16_9_1920_1x.jpg'
			: selectedImage;
	}

	// Fallback to mission relation with job image (3:2 format, 480 size) - client-side data
	if (publication.mission?.job?.image?.['3_2']?.['480'] && publication.objectId) {
		const indexOfImage = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz'.indexOf(publication.objectId[0]) % publication.mission.job.image['3_2']['480'].length;
		const imageData = publication.mission.job.image['3_2']['480'][indexOfImage];
		return imageData?.['1x'] || null;
	}

	// Check targetData for logo (Indeed integration)
	const indeedData = publication.targetData && 'indeed' in publication.targetData
		? publication.targetData.indeed
		: null;
	if (indeedData?.logo) {
		return indeedData.logo;
	}

	// Final fallback to feature image
	if (publication.mission?.feature?.objectId) {
		return `/images/feature/${publication.mission.feature.objectId}_3_2_480_1x.jpg`;
	}

	return null;
};
// #endregion

// #region TIME FORMATTING UTILITIES
/**
 * Format time difference to display relative time with i18n support
 * Handles proper display of days, hours and edge cases
 * @param createdAt - The creation date of the publication
 * @param t - Translation function from useTranslation
 * @returns Formatted time string
 */
export const formatTimeAgo = (createdAt: Date | string, _t: (key: string, options?: any) => string): string => {
	const now = new Date();
	const created = new Date(createdAt);
	const diffInMs = now.getTime() - created.getTime();

	// Convert to days and hours
	const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
	const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));

	// Fallback French text (since translations aren't working in this context)
	const fallbackText = {
		prefix: 'Ajouté il y a',
		lessThanHour: "moins d'une heure",
		hour: 'heure',
		hours: 'heures',
		day: 'jour',
		days: 'jours',
		moreThan30Days: 'plus de 30 jours',
	};

	// More than 30 days
	if (diffInDays > 30) {
		return fallbackText.moreThan30Days;
	}

	// Days
	if (diffInDays > 0) {
		const dayText = diffInDays === 1 ? fallbackText.day : fallbackText.days;
		return `${fallbackText.prefix} ${diffInDays} ${dayText}`;
	}

	// Hours
	if (diffInHours > 0) {
		const hourText = diffInHours === 1 ? fallbackText.hour : fallbackText.hours;
		return `${fallbackText.prefix} ${diffInHours} ${hourText}`;
	}

	// Less than an hour
	return `${fallbackText.prefix} ${fallbackText.lessThanHour}`;
};
// #endregion
